import React from 'react';
import {
  Autocomplete, TextField, Box, Typography, Popper, createFilterOptions, autocompleteClasses,
} from '@mui/material';
import { AccountBalanceWalletOutlined } from '@mui/icons-material';

/**
 * The account cell is often narrow (a dense line grid), and Autocomplete's
 * default popper is pinned to the anchor's width — which would crush the
 * code · name · type layout. This popper drops that width lock and gives the
 * list a comfortable minimum so options stay fully readable regardless of how
 * tight the input is.
 */
const WidePopper = (props: any) => (
  <Popper
    {...props}
    placement="bottom-start"
    style={{ ...(props.style || {}), width: 'auto' }}
    modifiers={[{ name: 'preventOverflow', options: { padding: 8 } }]}
    sx={{ [`& .${autocompleteClasses.paper}`]: { minWidth: 340, maxWidth: 520 } }}
  />
);

/**
 * Rich, readable Chart-of-Accounts picker used across Finance forms.
 *
 * Why a dedicated component:
 *  - The COA code must stay legible even on the *highlighted* option, where MUI
 *    paints a primary-tinted background — so the code is rendered in
 *    `text.primary` (never the accent colour, which washed out on that tint).
 *  - Options are grouped by account type and searchable by code OR name, so a
 *    user can type "1020" or "HDFC" and land on the same row.
 *  - Each option shows code · name · a type pill, and the selected value keeps
 *    its type pill as a leading adornment.
 */

export interface AccountOption {
  id: number | string;
  account_code: string;
  account_name: string;
  account_type?: string;
  current_balance?: number | string;
}

const TYPE_ORDER = ['ASSET', 'LIABILITY', 'EQUITY', 'INCOME', 'EXPENSE'];
const TYPE_TINT: Record<string, string> = {
  ASSET: '#059669', LIABILITY: '#dc2626', EQUITY: '#7c3aed', INCOME: '#0284c7', EXPENSE: '#ea580c',
};
const tintFor = (t?: string) => (t && TYPE_TINT[t]) || '#64748b';

const filter = createFilterOptions<AccountOption>({
  stringify: (a) => `${a.account_code} ${a.account_name} ${a.account_type || ''}`,
  trim: true,
});

interface Props {
  accounts: AccountOption[];
  value: string;                       // selected account id as string ('' = none)
  onChange: (id: string) => void;
  error?: boolean;
  helperText?: React.ReactNode;
  size?: 'small' | 'medium';
  placeholder?: string;
  autoFocus?: boolean;
}

const AccountSelect: React.FC<Props> = ({
  accounts, value, onChange, error, helperText, size = 'small',
  placeholder = 'Search by code or name…', autoFocus,
}) => {
  const selected = accounts.find((a) => String(a.id) === String(value)) || null;

  // Order options by type (Balance-Sheet first, then P&L) then by code, so the
  // grouped list reads like a chart of accounts rather than insertion order.
  const options = React.useMemo(() => {
    return [...accounts].sort((a, b) => {
      const ta = TYPE_ORDER.indexOf(a.account_type || '');
      const tb = TYPE_ORDER.indexOf(b.account_type || '');
      if (ta !== tb) return (ta === -1 ? 99 : ta) - (tb === -1 ? 99 : tb);
      return String(a.account_code).localeCompare(String(b.account_code), undefined, { numeric: true });
    });
  }, [accounts]);

  return (
    <Autocomplete<AccountOption>
      size={size}
      options={options}
      value={selected}
      onChange={(_, v) => onChange(v ? String(v.id) : '')}
      filterOptions={filter}
      PopperComponent={WidePopper}
      sx={{ minWidth: 190 }}
      groupBy={(a) => a.account_type || 'OTHER'}
      getOptionLabel={(a) => (a ? `${a.account_code} — ${a.account_name}` : '')}
      isOptionEqualToValue={(a, v) => String(a.id) === String(v.id)}
      renderGroup={(params) => (
        <li key={params.key}>
          <Box sx={{
            position: 'sticky', top: 0, zIndex: 1, px: 1.5, py: 0.5,
            bgcolor: 'background.paper', borderBottom: '1px solid', borderColor: 'divider',
            display: 'flex', alignItems: 'center', gap: 0.75,
          }}>
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: tintFor(params.group) }} />
            <Typography variant="caption" sx={{ fontWeight: 800, letterSpacing: '0.06em', color: tintFor(params.group) }}>
              {params.group}
            </Typography>
          </Box>
          <Box component="ul" sx={{ p: 0, m: 0 }}>{params.children}</Box>
        </li>
      )}
      renderOption={(props, a) => {
        const { key, ...rest } = props as any;
        return (
          <Box component="li" key={a.id} {...rest} sx={{
            // Keep code + name legible even when this row is highlighted: the
            // highlight tints the background, so text stays on text.primary.
            [`&.${autocompleteClasses.option}`]: { alignItems: 'center', gap: 1.25, py: 0.75 },
          }}>
            <Box component="span" sx={{ fontFamily: 'monospace', fontWeight: 700, color: 'text.primary', flexShrink: 0, minWidth: 52 }}>{a.account_code}</Box>
            <Box component="span" sx={{ flex: 1, minWidth: 0, color: 'text.primary', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.account_name}</Box>
            {typeof a.current_balance !== 'undefined' && a.current_balance !== null && (
              <Typography variant="caption" sx={{ flexShrink: 0, color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
                {new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(Number(a.current_balance) || 0)}
              </Typography>
            )}
          </Box>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          autoFocus={autoFocus}
          placeholder={placeholder}
          error={error}
          helperText={helperText}
          FormHelperTextProps={{ sx: { mx: 0, fontSize: '0.6875rem' } }}
          InputProps={{
            ...params.InputProps,
            startAdornment: selected ? (
              <Box sx={{ display: 'flex', alignItems: 'center', pl: 0.5, gap: 0.5 }}>
                <AccountBalanceWalletOutlined sx={{ fontSize: 16, color: tintFor(selected.account_type) }} />
              </Box>
            ) : params.InputProps.startAdornment,
          }}
        />
      )}
    />
  );
};

export default AccountSelect;
